<template>
    <DModal
        :show="showModal"
        overflow-auto
        :title="__('settings.microsoft365-settings.webhooks.title')"
        :message="__('settings.microsoft365-settings.webhooks.message')"
        @close="$emit('close-modal')"
        @confirm="submitForm"
    >
        <template #default>
            <DCard
                padding
                class="min-height"
            >
                <DFormRow
                    :label="__('settings.microsoft365-settings.webhooks.events')"
                    is-required
                    :error="errorsData.selected_webhook_events"
                    :hint="__('settings.microsoft365-settings.webhooks.events_hint')"
                >
                    <DMultiSelect
                        id="webhook_events"
                        :options="allWebhookOptions"
                        :selected="accountWebhookConfig.webhookEvents"
                        :selection="true"
                        selection-type="tags"
                        :search="true"
                        :disabled="isInLoadingState"
                        :is-valid="isFormFieldValid('webhook_events')"
                        @update="(values) => updateSelectedWebhookEvents(values)"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.microsoft365-settings.webhooks.webhook_url')"
                    is-required
                    :error="errorsData.webhook_url"
                    :hint="__('settings.microsoft365-settings.webhooks.webhook_url_hint')"
                >
                    <DInput
                        id="webhook_url"
                        v-model="form.webhook_url"
                        :disabled="isInLoadingState"
                        :is-valid="isFormFieldValid('webhook_url')"
                    />
                </DFormRow>
                <DFormRow
                    :label="__('settings.microsoft365-settings.webhooks.ms_signature_header')"
                    :hint="__('settings.microsoft365-settings.webhooks.ms_signature_header_hint')"
                    is-required
                    :error="errorsData.is_ms_signature_header"
                >
                    <DSwitch
                        id="is_ms_signature_header"
                        v-model="form.is_ms_signature_header"
                        color="success"
                        variant="3d"
                        :disabled="isInLoadingState"
                        :is-valid="isFormFieldValid('is_ms_signature_header')"
                    />
                </DFormRow>
            </DCard>
        </template>
    </DModal>
</template>

<script setup>

import { useForm } from '@inertiajs/vue3'
import { route } from '~@/Composables/route'
import { computed, ref, watch } from 'vue'

const props = defineProps({
    errors: {
        type: Object,
        default: () => { return {} }
    },
    showModal: {
        type: Boolean,
        default: false
    },
    allWebhooks: {
        type: Array,
        default () {
            return []
        }
    },
    accountWebhookConfig: {
        type: Object,
        default () {
            return {}
        }
    },
    accountId: {
        type: Number,
        default () {
            return null
        }
    }
})

const errorsData = ref(props.errors)

const form = useForm({
    account_id: props.accountId,
    subscriber_id: null,
    selected_webhook_events: [],
    webhook_url: '',
    is_ms_signature_header: false
})

watch([() => props.accountWebhookConfig, () => props.accountId], ([newConfig, accountId]) => {
    if (newConfig) {
        form.defaults({
            account_id: accountId,
            subscriber_id: newConfig.subscriberId ?? null,
            selected_webhook_events: newConfig.webhookEvents ?? [],
            webhook_url: newConfig.webhookUrl ?? '',
            is_ms_signature_header: newConfig.signatureTokenToMsSignatureHeader ?? false
        })
        form.reset()
    }
}, { immediate: true, deep: true })

const emit = defineEmits(['close-modal'])

const updateSelectedWebhookEvents = function (values) {
    form.selected_webhook_events = values
}

const allWebhookOptions = computed(() => {
    return props.allWebhooks.map((webhook) => (
        {
            text: webhook,
            value: webhook,
            selected: props.accountWebhookConfig.webhookEvents.includes(webhook),
        }
    ))
})

const submitForm = () => {
    form.post(route('settings.microsoft365-settings.webhooks.store'), {
        onSuccess: () => {
            emit('close-modal')
        },
        onError: (error) => {
            errorsData.value = error
        }
    })
}
</script>

<style scoped lang="scss">
:deep(.modal-box) {
    max-height: none !important;
}
.min-height {
    min-height: 400px;
}
</style>
